import { Badge, Dropdown, Flex, Space, Tag, Tooltip, Typography } from "antd"
import dayjs from "dayjs"
import utc from "dayjs/plugin/utc"

import ButtonComponent from "../../../../Components/Button"
import { titleSortFilters } from "../../../../Components/titleSortFilters"
import Icon from "../../../../Icon"
import Images from "../../../../Images"
import {
  alertsColumnIconMap,
  dateFormat,
  statusTextMap,
  tagColorMap,
  tagMap
} from "../../../../Utils/utils"

dayjs.extend(utc)

const { Text, Link } = Typography

const paymentSystemMap = {
  Stripe: "stripe",
  Adyen: "integration_adyen",
  Shopify: "shopify",
  "Bank Transfer": "bank"
}

export const getColumns = (options, handleChangeStatus) => ({
  status: {
    key: "status",
    sorter: true,
    title: (state) =>
      titleSortFilters({
        title: "Status",
        sorter: true,
        icon: alertsColumnIconMap["status"],
        stateData: state,
        key: "status"
      }),
    dataIndex: "status",
    fixed: "left",
    width: 120,
    render: (_, record) => {
      const isConfirmedBankTransferPending =
        _?.toLowerCase() === "pending" &&
        record?.payment_system === "Bank Transfer" &&
        !record?.can_confirm
      if (isConfirmedBankTransferPending) {
        return (
          <Dropdown
            disabled={!isConfirmedBankTransferPending}
            menu={{
              items: options?.map((el) => {
                let statusLowerCase = el?.value?.toLowerCase()
                return {
                  key: el?.value,
                  label: (
                    <Flex gap={8} align="center">
                      <Badge
                        status={tagMap[statusLowerCase] || "default"}
                        color={tagColorMap[el?.value]}
                      />
                      <Text>{statusTextMap[el?.value] || el?.label}</Text>
                    </Flex>
                  )
                }
              }),
              onClick: ({ key, domEvent }) => {
                domEvent.stopPropagation()
                if (key !== _) handleChangeStatus({ id: record.id, newStatus: key })
              }
            }}
          >
            <Tag
              color={tagMap[_?.toLowerCase()] || "default"}
              key={_}
              style={{ margin: 0 }}
              className={"simple large"}
            >
              <Flex gap={8} align="center">
                <Badge status={tagMap[_?.toLowerCase()] || "default"} color={tagColorMap[_]} />
                <Text>{statusTextMap[_] || _}</Text>

                <Icon type="chevron_down_small" style={{ marginLeft: -4 }} />
              </Flex>
            </Tag>
          </Dropdown>
        )
      } else {
        return (
          <Tag
            color={tagMap[_?.toLowerCase()] || "default"}
            key={_}
            style={{ margin: 0 }}
            className={"simple large"}
          >
            <Flex gap={8} align="center">
              <Badge status={tagMap[_?.toLowerCase()] || "default"} color={tagColorMap[_]} />
              <Text>{statusTextMap[_] || _}</Text>
            </Flex>
          </Tag>
        )
      }
    }
  },
  email: {
    key: "email",
    sorter: true,
    title: (state) =>
      titleSortFilters({
        title: "User email",
        sorter: true,
        icon: alertsColumnIconMap["email"],
        stateData: state,
        key: "email"
      }),
    dataIndex: "email",
    width: 185
  },
  full_name: {
    key: "full_name",
    sorter: true,
    title: (state) =>
      titleSortFilters({
        title: "User full name",
        sorter: true,
        icon: alertsColumnIconMap["full_name"],
        stateData: state,
        key: "full_name"
      }),
    dataIndex: "full_name",
    width: 185
  },
  payment_system: {
    key: "payment_system",
    sorter: true,
    title: (state) =>
      titleSortFilters({
        title: "Payment system",
        sorter: true,
        icon: alertsColumnIconMap["payment_system"],
        stateData: state,
        key: "payment_system"
      }),
    dataIndex: "payment_system",
    width: 185,
    render: (_) => {
      return (
        <Space>
          {paymentSystemMap[_] && (
            <img src={Images[paymentSystemMap[_]] || _} width={20} height={20} alt={""} />
          )}
          <Text>{_ || "—"}</Text>
        </Space>
      )
    }
  },
  business_name: {
    key: "business_name",
    sorter: true,
    title: (state) =>
      titleSortFilters({
        title: "Business account",
        sorter: true,
        icon: alertsColumnIconMap["business_name"],
        stateData: state,
        key: "business_name"
      }),
    dataIndex: "business_name",
    ellipsis: true,
    width: 185
  },
  alerts_period: {
    key: "alerts_period",
    sorter: true,
    title: (state) =>
      titleSortFilters({
        title: "Alerts period",
        sorter: true,
        icon: alertsColumnIconMap["alerts_period"],
        stateData: state,
        key: "alerts_period"
      }),
    dataIndex: "alerts_period",
    ellipsis: true,
    width: 210,
    render: (_) => _ || "—"
  },
  invoice_amount: {
    key: "invoice_amount",
    sorter: true,
    title: (state) =>
      titleSortFilters({
        title: "Invoice amount",
        sorter: true,
        icon: alertsColumnIconMap["invoice_amount"],
        stateData: state,
        key: "invoice_amount"
      }),
    dataIndex: "invoice_amount",
    width: 175,
    render: (_, record) => <Text>{`${record?.currency || "$"} ${_}`}</Text>
  },
  invoice_date: {
    key: "invoice_date",
    sorter: true,
    title: (state) =>
      titleSortFilters({
        title: "Invoice date",
        sorter: true,
        icon: alertsColumnIconMap["invoice_date"],
        stateData: state,
        key: "invoice_date"
      }),
    dataIndex: "invoice_date",
    width: 155,
    render: (_) => (
      <Space direction="vertical" size={4}>
        <Text>{dayjs(_).format(dateFormat)}</Text>
        <Text type="secondary">{dayjs(_).format("h:mm A [(UTC)]")}</Text>
      </Space>
    )
  },
  invoice_id: {
    key: "invoice_id",
    sorter: true,
    title: (state) =>
      titleSortFilters({
        title: "Invoice ID",
        sorter: true,
        icon: alertsColumnIconMap["invoice_id"],
        stateData: state,
        key: "invoice_id"
      }),
    dataIndex: "invoice_id",
    width: 185,
    render: (_) => _ || <Text style={{ display: "block", textAlign: "center" }}>—</Text>
  },
  // is_reissued: {
  //   key: 'is_reissued',
  //   sorter: true,
  //   title: (state) =>
  //     titleSortFilters({
  //       title: 'Reissued',
  //       sorter: true,
  //       icon: alertsColumnIconMap['is_reissued'],
  //       stateData: state,
  //       key: 'is_reissued',
  //     }),
  //   dataIndex: 'is_reissued',
  //   width: 130,
  //   render: (_) => _ ? 'Yes' : 'No'
  // },
  invoice_link: {
    key: "invoice_link",
    title: (state) =>
      titleSortFilters({
        title: "Invoice link",
        icon: alertsColumnIconMap["invoice_link"],
        stateData: state,
        key: "invoice_link"
      }),
    dataIndex: "invoice_link",
    minWidth: 140,
    render: (_) => (
      <Flex gap={4} align="center">
        <Text copyable={{ tooltips: false, text: _ }}></Text>
        <a href={_} target="_blank" rel="noopener noreferrer">
          link
        </a>
      </Flex>
    )
  },
  loyal_billing: {
    key: "loyal_billing",
    sorter: true,
    title: (state) =>
      titleSortFilters({
        title: "Loyal billing",
        sorter: true,
        icon: alertsColumnIconMap["loyal_billing"],
        stateData: state,
        key: "loyal_billing"
      }),
    dataIndex: "loyal_billing",
    width: 150,
    render: (_) => (_ ? "Yes" : "No")
  }
  // stripe_charged: {
  //       key: 'stripe_charged',
  //       sorter: true,
  //       title: (state) =>
  //         titleSortFilters({
  //           title: 'Charge By',
  //           sorter: true,
  //           icon: alertsColumnIconMap['stripe_charged'],
  //           stateData: state,
  //           key: 'stripe_charged',
  //         }),
  //       dataIndex: 'stripe_charged',
  //       width: 140,
  //       render: (_) => _ ? _ : 'None'
  // },
  // charge_by: {
  //       key: 'charge_by',
  //       sorter: true,
  //       title: (state) =>
  //         titleSortFilters({
  //           title: 'Charge By',
  //           sorter: true,
  //           icon: alertsColumnIconMap['charge_by'],
  //           stateData: state,
  //           key: 'charge_by',
  //         }),
  //       dataIndex: 'charge_by',
  //       width: 140,
  //       render: (_) => _ ? _ : 'None'
  // },
})

export const getDefaultColumn = ({ field_id, label }) => ({
  key: field_id,
  sorter: true,
  title: (state) =>
    titleSortFilters({
      title: label,
      sorter: true,
      icon: alertsColumnIconMap[field_id],
      stateData: state,
      key: field_id
    }),
  dataIndex: field_id,
  width: 185,
  ellipsis: true
})

export const getActionColumn = ({ onSelectRow }) => {
  const allowedDownloadStatuses = ["paid", "failed", "declined"]
  const isBankTransferPending = (record) =>
    record?.payment_system === "Bank Transfer" && record?.status?.toLowerCase() === "pending"
  const isDownloadInvoice = (record) =>
    record?.status?.toLowerCase() === "pending" && !record?.can_confirm && record?.loyal_billing

  const handleAction = (e, data, type) => {
    e.preventDefault()
    e.stopPropagation()
    onSelectRow(data, type)
  }
  return {
    key: "actions",
    title: "Actions",
    width: 100,
    render: (_, record) => (
      <Flex>
        {isBankTransferPending(record) && record?.can_confirm && (
          <Tooltip title="Confirm the invoice">
            <ButtonComponent
              type="link"
              icon={<Icon type={"check"} />}
              onClick={(e) => handleAction(e, record, "confirm_invoice_amount")}
              className="flex center "
            />
          </Tooltip>
        )}

        {record?.invoice_comment && (
          <Tooltip title="Comment">
            <ButtonComponent
              type="link"
              icon={<Icon type={"comment"} />}
              onClick={(e) => handleAction(e, record, "view_comment")}
              className="flex center "
            />
          </Tooltip>
        )}

        {isBankTransferPending(record) && record?.can_change_amount && (
          <Tooltip title="Change the amount and confirm invoice">
            <ButtonComponent
              type="link"
              icon={<Icon type={"edit_big_2"} />}
              onClick={(e) => handleAction(e, record, "change_amount")}
              className="flex center "
            />
          </Tooltip>
        )}

        {(allowedDownloadStatuses.includes(record?.status?.toLowerCase()) ||
          isDownloadInvoice(record)) && (
          <Tooltip title="Download invoice">
            <ButtonComponent
              type="link"
              icon={<Icon type={"arrow_inbox"} />}
              onClick={(e) => handleAction(e, record, "download_invoice")}
              className="flex center "
            />
          </Tooltip>
        )}
      </Flex>
    )
  }
}
